import { BaseScreen, i18n, waitFor } from '@goodboydigital/astro';
import gsap from 'gsap';
import { Point, Text, Ticker } from 'pixi.js';

import { App, Shared } from '../App';
import { Settings } from '../AppConfig';
import { AnalyticsState } from '../plugins/AnalyticsPlugin';
import { SquareButton } from '../ui/buttons/SquareButton';
import LocalisedText from '../ui/LocalisedText';
import { Panel } from '../ui/Panel';
import { RankBadge } from '../ui/RankBadge';
import { RankPointsLabel } from '../ui/RankPointsLabel';
import { RankTitleLabel } from '../ui/RankTitleLabel';
import { UserIdentity } from '../ui/UserIdentity';
import { Maths } from '../utils/Maths';

export class RankScreen extends BaseScreen
{
    public app: App;

    private panel!: Panel;
    private badge!: RankBadge;
    private identity?: UserIdentity;
    private rankTitle!: RankTitleLabel;
    private rankPoints!: RankPointsLabel;
    private textRankUp!: LocalisedText;
    private textPoints!: Text;
    private textNextRankPoints!: LocalisedText;
    private rankedUp: boolean;
    private continueButton!: SquareButton;

    constructor(app: App)
    {
        super(app);
        this.app = app;
        this.rankedUp = false;
    }

    async init(): Promise<void>
    {
        this.panel = new Panel({
            w: 720,
            h: 540,
        });
        this.view.addChild(this.panel.view);
        
        if(!Settings.browserLite)
        {
            this.identity = new UserIdentity();
            this.panel.container.addChild(this.identity);
            this.identity.y = -200;
        }
        
        this.rankTitle = new RankTitleLabel();
        this.panel.container.addChild(this.rankTitle);
        this.rankTitle.y = -160;

        this.badge = new RankBadge(200);
        this.panel.container.addChild(this.badge);

        this.rankPoints = new RankPointsLabel();
        this.panel.container.addChild(this.rankPoints);
        this.rankPoints.x = 230;

        this.textPoints = new Text('', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            align: 'left',
            fontSize: 36,
            stroke: 0x333333,
            strokeThickness: 10,
            lineJoin: 'round',
        });
        this.textPoints.anchor.set(0.5);
        this.panel.container.addChild(this.textPoints);
        this.textPoints.x = -this.rankPoints.x;

        this.textRankUp = new LocalisedText('', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            align: 'left',
            fontSize: 50,
            stroke: 0x333333,
            strokeThickness: 10,
            lineJoin: 'round',
            maxWidth: 400
        });
        this.textRankUp.anchor.set(0.5);
        this.panel.container.addChild(this.textRankUp);
        this.textRankUp.y = 170;

        this.textNextRankPoints = new LocalisedText('', {
            fontFamily: 'lubalin',
            fill: 0x90C9E3,
            align: 'left',
            fontSize: 26,
            stroke: 0x333333,
            strokeThickness: 8,
            lineJoin: 'round',
            maxWidth: 400
        });
        this.textNextRankPoints.anchor.set(0.5);
        this.panel.container.addChild(this.textNextRankPoints);
        this.textNextRankPoints.y = this.textRankUp.y;

        this.continueButton = new SquareButton({
            icon: 'icon-play',
            startHidden: true,
        });
        this.panel.container.addChild(this.continueButton.view);
        this.continueButton.onPress.connect(() =>
        {
            this.app.controller.toResults();
        });
        this.continueButton.view.x = (this.panel.bg.width / 2) - 80;
        this.continueButton.view.y = (this.panel.bg.height / 2) - 80;
    }

    show(): void
    {
        this.resize(this.width, this.height);
        this.view.visible = true;
        this.panel.turnOffShining();

        this.textRankUp.setText('rankScreen_rankUp');
        this.textRankUp.visible = false;
        this.textNextRankPoints.visible = true;
        this.rankedUp = false;
        this.continueButton.hide(false);
        this.animate();
    }

    hidden(): void
    {
        this.view.visible = false;
    }

    resize(w: number, h: number): void
    {
        super.resize(w, h);
        this.view.x = (this.width - this.app.width) * 0.5;
        this.view.y = (this.height - this.app.height) * 0.5;
        this.panel.view.position.copyFrom(this.app.stageCenter as Point);
    }

    private refresh(animDuration = 0): void
    {
        const status = this.app.rank.getRankStatus();
        
        if(this.identity)
        {
            this.identity.refresh();
        }
        
        this.badge.refresh(status, animDuration);
        this.rankTitle.refresh(status.title, status.tier, animDuration > 0);
        this.rankPoints.refresh(status.points);

        this.textNextRankPoints.setText('rankScreen_nextRankPoints', { points: status.nextRankPoints });

        if (this.rankedUp) this.playRankUpAnimation();
    }

    private async animate(): Promise<void>
    {
        this.rankedUp = false;

        // Test rank screen animation
        if (Settings.to === 'rank')
        {
            this.app.rank.lastPointsEarned = 30;
        }

        let points = this.app.rank.lastPointsEarned;

        // Temporary reduce current rank points so that can be increased one by one
        this.app.user.setRankPoints(this.app.user.getRankPoints() - points, false);

        this.refresh();

        if (points === 1)
        {
            this.textPoints.text = i18n.translate('rankScreen_plusPoints_singular', { points });
        }
        else
        {
            this.textPoints.text = i18n.translate('rankScreen_plusPoints', { points });
        }

        await waitFor(500);

        const stepDuration = Maths.clamp((10 - points) * 0.1, 0.15, 1);

        while (points--)
        {
            await this.animatioStep(stepDuration);
        }

        this.continueButton.show(true);
    }

    private async animatioStep(duration = 0.1): Promise<void>
    {
        const points = this.app.user.getRankPoints() + 1;

        this.app.user.setRankPoints(points, false);

        const status = this.app.rank.getRankStatus();

        if (status.progress === 0) this.rankedUp = true;

        this.refresh(duration);
        Shared.game.soundSystem.playSfx('slide');
        await waitFor(1000 * duration);
    }

    public update(): void
    {
        if (this.textRankUp.visible)
        {
            this.textRankUp.rotation = Math.sin(Ticker.system.lastTime * 0.01) * 0.1;
        }
    }

    public playRankUpAnimation(): void
    {
        if (this.textRankUp.visible) return;
        this.textRankUp.visible = true;
        this.panel.turnOnShining();

        var oldScaleX = this.textRankUp.scale.x;
        var oldScaleY = this.textRankUp.scale.y;
        this.textRankUp.scale.set(0);
        gsap.to(this.textRankUp.scale, { x: oldScaleX, y: oldScaleY, duration: 1, ease: 'elastic.out' });
        this.textNextRankPoints.visible = false;

        Shared.game.soundSystem.playSfx('race-end');
        Shared.game.soundSystem.playSfx('trick-land');
        Shared.game.soundSystem.playSfx('drop-landing');
        
        this.app.analytics.trackState(AnalyticsState.RACE_REWARDS, { content_type: "rank up" });
    }
}
